/**
 * Nigerian State, LGA, and Area Geolocation Coordinate Lookup & Validation
 * Provides verified Nigerian administrative centroids, coordinate bounds validation,
 * bounding-box pre-filtering, and Haversine distance calculation.
 * Never fabricates random coordinates or applies arbitrary jitter.
 */

export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface BoundingBox {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export const NIGERIA_CENTER: GeoPoint = {
  lat: 9.082,
  lng: 8.6753,
};

export const NIGERIA_BOUNDS = {
  minLat: 4.0,
  maxLat: 14.2,
  minLng: 2.5,
  maxLng: 15.2,
};

// State-level reference coordinates across Nigeria
export const NIGERIA_STATE_COORDINATES: Record<string, GeoPoint> = {
  'Abia': { lat: 5.4527, lng: 7.5248 },
  'Adamawa': { lat: 9.3265, lng: 12.3984 },
  'Akwa Ibom': { lat: 5.0077, lng: 7.8536 },
  'Anambra': { lat: 6.2209, lng: 6.9370 },
  'Bauchi': { lat: 10.3158, lng: 9.8442 },
  'Bayelsa': { lat: 4.9267, lng: 6.2676 },
  'Benue': { lat: 7.7322, lng: 8.5391 },
  'Borno': { lat: 11.8333, lng: 13.1500 },
  'Cross River': { lat: 4.9757, lng: 8.3417 },
  'Delta': { lat: 6.1982, lng: 6.7314 },
  'Ebonyi': { lat: 6.3249, lng: 8.1137 },
  'Edo': { lat: 6.3350, lng: 5.6037 },
  'Ekiti': { lat: 7.6211, lng: 5.2215 },
  'Enugu': { lat: 6.4584, lng: 7.5464 },
  'Federal Capital Territory': { lat: 9.0765, lng: 7.3986 },
  'Federal Capital Territory (FCT)': { lat: 9.0765, lng: 7.3986 },
  'FCT': { lat: 9.0765, lng: 7.3986 },
  'Abuja': { lat: 9.0765, lng: 7.3986 },
  'Gombe': { lat: 10.2897, lng: 11.1711 },
  'Imo': { lat: 5.4836, lng: 7.0333 },
  'Jigawa': { lat: 11.7564, lng: 9.3453 },
  'Kaduna': { lat: 10.5105, lng: 7.4165 },
  'Kano': { lat: 12.0022, lng: 8.5920 },
  'Katsina': { lat: 12.9908, lng: 7.6018 },
  'Kebbi': { lat: 12.4539, lng: 4.1975 },
  'Kogi': { lat: 7.8023, lng: 6.7333 },
  'Kwara': { lat: 8.4966, lng: 4.5421 },
  'Lagos': { lat: 6.5244, lng: 3.3792 },
  'Nasarawa': { lat: 8.4904, lng: 8.5200 },
  'Niger': { lat: 9.6139, lng: 6.5569 },
  'Ogun': { lat: 7.1475, lng: 3.3619 },
  'Ondo': { lat: 7.2571, lng: 5.2058 },
  'Osun': { lat: 7.7827, lng: 4.5418 },
  'Oyo': { lat: 7.3775, lng: 3.9470 },
  'Plateau': { lat: 9.8965, lng: 8.8583 },
  'Rivers': { lat: 4.8156, lng: 7.0498 },
  'Sokoto': { lat: 13.0059, lng: 5.2476 },
  'Taraba': { lat: 8.8937, lng: 11.3596 },
  'Yobe': { lat: 11.7480, lng: 11.9660 },
  'Zamfara': { lat: 12.1704, lng: 6.6641 },
};

// Detailed LGA & popular neighborhood coordinates in major Nigerian commercial hubs
export const NIGERIA_AREA_COORDINATES: Record<string, GeoPoint> = {
  // Lagos State LGAs & Towns
  'ikeja': { lat: 6.6018, lng: 3.3515 },
  'allen avenue': { lat: 6.6041, lng: 3.3502 },
  'alausa': { lat: 6.6143, lng: 3.3581 },
  'alausa (secretariat)': { lat: 6.6143, lng: 3.3581 },
  'ogba': { lat: 6.6289, lng: 3.3411 },
  'maryland': { lat: 6.5724, lng: 3.3679 },
  'eti-osa': { lat: 6.4474, lng: 3.4723 },
  'lekki': { lat: 6.4474, lng: 3.4723 },
  'lekki phase 1': { lat: 6.4478, lng: 3.4723 },
  'victoria island': { lat: 6.4281, lng: 3.4219 },
  'ikoyi': { lat: 6.4549, lng: 3.4346 },
  'ajah': { lat: 6.4698, lng: 3.5852 },
  'ibeju-lekki': { lat: 6.4651, lng: 3.7483 },
  'surulere': { lat: 6.4979, lng: 3.3536 },
  'bode thomas': { lat: 6.4912, lng: 3.3547 },
  'lagos mainland': { lat: 6.5059, lng: 3.3779 },
  'yaba': { lat: 6.5158, lng: 3.3752 },
  'lagos island': { lat: 6.4541, lng: 3.3947 },
  'kosofe': { lat: 6.5821, lng: 3.3849 },
  'gbagada': { lat: 6.5539, lng: 3.3868 },
  'magodo': { lat: 6.6194, lng: 3.3792 },
  'alimosho': { lat: 6.6106, lng: 3.2958 },
  'egbeda': { lat: 6.5973, lng: 3.2916 },
  'ikotun': { lat: 6.5443, lng: 3.2638 },
  'agege': { lat: 6.6180, lng: 3.3209 },
  'ifako-ijaiye': { lat: 6.6524, lng: 3.3067 },
  'oshodi-isolo': { lat: 6.5355, lng: 3.3087 },
  'mushin': { lat: 6.5298, lng: 3.3541 },
  'shomolu': { lat: 6.5392, lng: 3.3842 },
  'apapa': { lat: 6.4488, lng: 3.3590 },
  'amuwo-odofin': { lat: 6.4660, lng: 3.2841 },
  'festac': { lat: 6.4664, lng: 3.2835 },
  'ojo': { lat: 6.4612, lng: 3.1873 },
  'badagry': { lat: 6.4150, lng: 2.8813 },
  'ikorodu': { lat: 6.6194, lng: 3.5105 },
  'epe': { lat: 6.5841, lng: 3.9834 },

  // Abuja (FCT)
  'abuja municipal area council': { lat: 9.0579, lng: 7.4951 },
  'amac': { lat: 9.0579, lng: 7.4951 },
  'wuse': { lat: 9.0667, lng: 7.4692 },
  'wuse ii': { lat: 9.0787, lng: 7.4762 },
  'maitama': { lat: 9.0882, lng: 7.4934 },
  'garki': { lat: 9.0319, lng: 7.4828 },
  'asokoro': { lat: 9.0467, lng: 7.5236 },
  'jabi': { lat: 9.0642, lng: 7.4225 },
  'gwarinpa': { lat: 9.1099, lng: 7.4042 },
  'lugbe': { lat: 8.9756, lng: 7.3752 },
  'kubwa': { lat: 9.1538, lng: 7.3220 },
  'bwari': { lat: 9.2869, lng: 7.3809 },
  'gwagwalada': { lat: 8.9430, lng: 7.0837 },
  'kuje': { lat: 8.8795, lng: 7.2276 },

  // Rivers (Port Harcourt)
  'port harcourt': { lat: 4.8156, lng: 7.0498 },
  'obio/akpor': { lat: 4.8472, lng: 6.9746 },
  'eleme': { lat: 4.7931, lng: 7.1233 },

  // Delta (Asaba, Warri)
  'oshimili south': { lat: 6.1982, lng: 6.7314 },
  'asaba': { lat: 6.1982, lng: 6.7314 },
  'oshimili north': { lat: 6.2625, lng: 6.6634 },
  'okpanam': { lat: 6.2368, lng: 6.6901 },
  'warri south': { lat: 5.5167, lng: 5.7500 },
  'effurun': { lat: 5.5544, lng: 5.7932 },

  // Oyo (Ibadan)
  'ibadan north': { lat: 7.4069, lng: 3.9059 },
  'ibadan south-west': { lat: 7.3621, lng: 3.8647 },
  'bodija': { lat: 7.4223, lng: 3.9114 },

  // Kano
  'kano municipal': { lat: 11.9964, lng: 8.5167 },
  'nasarawa (kano)': { lat: 12.0124, lng: 8.5542 },

  // Enugu
  'enugu north': { lat: 6.4584, lng: 7.5464 },
  'nsukka': { lat: 6.8578, lng: 7.3958 },

  // Edo (Benin City)
  'oredo': { lat: 6.3350, lng: 5.6037 },
  'benin city': { lat: 6.3350, lng: 5.6037 },
};

/**
 * Validates that latitude and longitude are finite numbers within valid WGS84 range
 * and not a bogus (0, 0) null-island coordinate.
 */
export function isValidCoordinatePair(lat: unknown, lng: unknown): boolean {
  const numLat = typeof lat === 'number' ? lat : Number(lat);
  const numLng = typeof lng === 'number' ? lng : Number(lng);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLng)) return false;
  if (numLat < -90 || numLat > 90) return false;
  if (numLng < -180 || numLng > 180) return false;
  if (Math.abs(numLat) < 0.0001 && Math.abs(numLng) < 0.0001) return false;
  return true;
}

/**
 * Checks whether a coordinate pair lies within or immediately adjacent to Nigeria's geographic bounding box.
 */
export function isWithinNigeriaBounds(lat: number, lng: number): boolean {
  if (!isValidCoordinatePair(lat, lng)) return false;
  return (
    lat >= NIGERIA_BOUNDS.minLat &&
    lat <= NIGERIA_BOUNDS.maxLat &&
    lng >= NIGERIA_BOUNDS.minLng &&
    lng <= NIGERIA_BOUNDS.maxLng
  );
}

/**
 * Computes a geographic bounding box around a center point for a given radius in km.
 * Used as a fast pre-filter before running exact Haversine calculations.
 */
export function computeBoundingBox(lat: number, lng: number, radiusKm: number): BoundingBox {
  const latDelta = radiusKm / 111.32;
  const cosLat = Math.max(0.1, Math.cos((lat * Math.PI) / 180));
  const lngDelta = radiusKm / (111.32 * cosLat);

  return {
    minLat: lat - latDelta,
    maxLat: lat + latDelta,
    minLng: lng - lngDelta,
    maxLng: lng + lngDelta,
  };
}

/**
 * Resolves a professional's validated service-base coordinates.
 * Priority:
 * 1. Explicit validated serviceLatitude / serviceLongitude
 * 2. Explicit validated latitude / longitude or locationLatitude / locationLongitude
 * 3. Deterministic Nigerian LGA / CityArea centroid ONLY when a valid Nigerian location is present on the profile.
 * Never invents random coordinates or applies synthetic jitter.
 */
export function resolveProfessionalCoordinates(pro: {
  id?: string;
  state?: string;
  lga?: string;
  cityArea?: string;
  latitude?: number;
  longitude?: number;
  locationLatitude?: number;
  locationLongitude?: number;
  serviceLatitude?: number;
  serviceLongitude?: number;
}): GeoPoint {
  const resolved = resolveValidatedProfessionalCoordinates(pro);
  return resolved || NIGERIA_CENTER;
}

/**
 * Strict coordinate resolver for nearby search that returns null if a professional
 * has invalid coordinates or no recognizable Nigerian service location.
 */
export function resolveValidatedProfessionalCoordinates(pro: {
  id?: string;
  state?: string;
  lga?: string;
  cityArea?: string;
  latitude?: number;
  longitude?: number;
  locationLatitude?: number;
  locationLongitude?: number;
  serviceLatitude?: number;
  serviceLongitude?: number;
}): (GeoPoint & { source: 'verified_pin' | 'lga_centroid' }) | null {
  // If any explicit coordinate field is provided on the record, it MUST be valid
  const explicitPairs: Array<[unknown, unknown]> = [
    [pro.serviceLatitude, pro.serviceLongitude],
    [pro.latitude, pro.longitude],
    [pro.locationLatitude, pro.locationLongitude],
  ];

  for (const [rawLat, rawLng] of explicitPairs) {
    const hasLat = rawLat !== undefined && rawLat !== null && rawLat !== '';
    const hasLng = rawLng !== undefined && rawLng !== null && rawLng !== '';
    if (hasLat || hasLng) {
      if (!isValidCoordinatePair(rawLat, rawLng)) {
        return null;
      }
      return {
        lat: Number(Number(rawLat).toFixed(5)),
        lng: Number(Number(rawLng).toFixed(5)),
        source: 'verified_pin',
      };
    }
  }

  // Try City Area lookup in Nigeria
  if (pro.cityArea) {
    const areaKey = pro.cityArea.trim().toLowerCase();
    if (NIGERIA_AREA_COORDINATES[areaKey]) {
      return {
        ...NIGERIA_AREA_COORDINATES[areaKey],
        source: 'lga_centroid',
      };
    }
  }

  // Try LGA lookup in Nigeria
  if (pro.lga) {
    const lgaKey = pro.lga.trim().toLowerCase();
    if (NIGERIA_AREA_COORDINATES[lgaKey]) {
      return {
        ...NIGERIA_AREA_COORDINATES[lgaKey],
        source: 'lga_centroid',
      };
    }
  }

  // Try State lookup if LGA is unspecified
  if (pro.state && NIGERIA_STATE_COORDINATES[pro.state]) {
    return {
      ...NIGERIA_STATE_COORDINATES[pro.state],
      source: 'lga_centroid',
    };
  }

  return null;
}

/**
 * Resolves center & zoom for a given state and LGA filter
 */
export function resolveFilterMapCenter(state?: string, lga?: string): { center: GeoPoint; zoom: number } {
  if (lga) {
    const lgaKey = lga.trim().toLowerCase();
    if (NIGERIA_AREA_COORDINATES[lgaKey]) {
      return {
        center: NIGERIA_AREA_COORDINATES[lgaKey],
        zoom: 13,
      };
    }
  }

  if (state && NIGERIA_STATE_COORDINATES[state]) {
    return {
      center: NIGERIA_STATE_COORDINATES[state],
      zoom: 11,
    };
  }

  return {
    center: NIGERIA_CENTER,
    zoom: 6,
  };
}

/**
 * Calculates great-circle distance in kilometers between two coordinates using the Haversine formula.
 * Documented formula:
 *   a = sin²(Δφ/2) + cos(φ1)·cos(φ2)·sin²(Δλ/2)
 *   c = 2·atan2(√a, √(1−a))
 *   d = R·c  (where R = 6371 km)
 */
export function calculateHaversineDistanceKm(a: GeoPoint, b: GeoPoint): number {
  const R = 6371; // Earth radius in km
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      sinDLng *
      sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(Math.max(0, 1 - h)));
  return Math.round(R * c * 100) / 100;
}
